import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { CONTACT, IMG } from "@/data/site";
import { Reveal, Eyebrow } from "@/components/Reveal";
import Seo from "@/components/Seo";

export default function Location() {
  const { t, lang } = useLang();
  const l = t.locationPage;

  const blocks = [
    { title: l.churchT, text: l.churchX, img: IMG.church, alt: "Crkva sv. Jakova u Međugorju" },
    { title: l.hillT, text: l.hillX, img: "/images/podbrdo.jpg", alt: "Brdo ukazanja Podbrdo u Međugorju" },
    { title: l.crossT, text: l.crossX, img: "/images/krizevac.jpg", alt: "Križevac iznad Međugorja" },
    { title: l.parkingT, text: l.parkingX, img: IMG.house, alt: "Sobe Bevanda — obiteljska kuća i parking" },
  ];

  return (
    <div data-testid="location-page" key={lang} className="pt-16 sm:pt-20">
      <Seo title={t.seo.location.title} description={t.seo.location.desc} image="/images/st-james-church.jpg" />

      <header className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-14 sm:pt-20 pb-12">
        <Reveal>
          <Eyebrow>Sobe Bevanda — Međugorje</Eyebrow>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1F2421]">{l.title}</h1>
          <p className="text-lg sm:text-xl text-[#4A504B] font-light leading-relaxed mt-6 max-w-2xl">{l.lead}</p>
        </Reveal>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {blocks.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.07}>
              <article
                data-testid={`location-block-${i}`}
                className="group bg-white rounded-2xl overflow-hidden border border-[#1F2421]/8 hover:shadow-[0_20px_50px_-20px_rgba(31,36,33,0.25)] transition-shadow duration-500"
              >
                <div className="overflow-hidden aspect-[16/9]">
                  <img
                    src={b.img}
                    alt={b.alt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                </div>
                <div className="p-6 sm:p-8">
                  <h2 className="font-serif-display text-2xl sm:text-3xl text-[#1F2421]">{b.title}</h2>
                  <p className="text-sm sm:text-base text-[#4A504B] leading-relaxed mt-3">{b.text}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#F3EFE6] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#1F2421]">{t.location.title}</h2>
            <p className="text-base sm:text-lg text-[#4A504B] leading-relaxed mt-5 max-w-lg">{t.location.text}</p>
            <ul className="mt-7 space-y-4">
              {[t.location.point1, t.location.point2, t.location.point3].map((p, i) => (
                <li key={i} className="flex items-center gap-3 text-sm sm:text-base text-[#1F2421]">
                  <span className="h-2 w-2 rounded-full bg-[#4A5D3E] shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-4 mt-9">
              <a
                href={CONTACT.directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-directions-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-[#4A5D3E] hover:bg-[#3A4A31] text-[#F9F8F3] text-sm font-semibold px-6 py-3 transition-colors duration-300"
              >
                {t.cta.directions}
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <Link
                to="/kontakt"
                data-testid="location-inquiry-cta"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-[#4A5D3E] hover:text-[#3A4A31] transition-colors"
              >
                {t.cta.sendInquiry}
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-2xl border-8 border-white shadow-[0_30px_60px_-30px_rgba(31,36,33,0.35)]">
              <iframe
                title="Sobe Bevanda — Međugorje map"
                src={CONTACT.mapEmbed}
                className="w-full h-[320px] sm:h-[400px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
